import React, { useState } from 'react';
import { Table, Tag, Drawer, Row, Col, Button, Typography, Space, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { createUseStyles } from 'react-jss';
import type { SimulationRawResult, SimulationShipmentRawResult } from '../../store/simulation/types';
import type { ShipiumTagConfig } from '../../components/shared/ShipiumTag';
import { ShipiumTagWithConfig } from '../../components/shared/ShipiumTag';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { useStoreState } from '../../store';
import StyledCodeMirror from '../../components/shared/StyledCodeMirror';
import { FileSearchOutlined } from '@ant-design/icons';
import { palette } from '@shipium-corp/shipium-ui-theme';
import { useCarrierServiceMethods } from '../../hooks/queries/useCarriersFiltered';

const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
    },
    '&& table > .ant-table-tbody > tr > td': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
    // disable hover
    '& .ant-table-tbody > tr.ant-table-row:hover > td': {
      background: 'unset',
    },
  },
  buttonLink: {
    fontFamily: 'monospace',
    fontSize: '1em',
    padding: 0,
  },
});

const { Text } = Typography;

export interface SimulationRawResultsTableProps {
  simulationRawResult?: SimulationRawResult;
}
export const SimulationRawResultsTable = ({ simulationRawResult }: SimulationRawResultsTableProps) => {
  const loading = useStoreState((state) => state.loading.loadingFlags.simulation);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | undefined>(undefined);
  const carrierServiceMethodMap = useCarrierServiceMethods();
  const classes = useStyles();
  const [query, setQuery] = useSearchParams();
  const simulationShipments = simulationRawResult?.simulationShipments || [];
  const selectedSimulatedShipment = simulationShipments.find((shipment) => {
    return shipment.key === selectedShipmentId;
  });

  const selectShipment = (shipment: SimulationShipmentRawResult) => {
    setSelectedShipmentId(shipment.key);
  };

  const clearShipmentSelection = () => {
    setSelectedShipmentId(undefined);
  };

  const gotoPage = (page: number) => {
    // TODO verify page is valid
    query.set('page', page.toString());
    setQuery(query);
  };

  // if (selectedShipmentId && !selectedSimulatedShipment) {
  //   clearShipmentSelection();
  // }

  const shipiumTagMapStatus: Record<string, ShipiumTagConfig> = {
    skipped: { color: 'Fuschia', label: 'Skipped', dark: true, icon: 'warning' },
    error: { color: 'Coral', label: 'Error', dark: true, icon: 'exclamation' },
    success: { color: 'Teal', label: 'Success', dark: true, icon: 'check' },
  };

  const errorColumns: ColumnsType<SimulationShipmentRawResult> = [
    {
      title: 'Error',
      key: 'error',
      align: 'left',
      width: 400,
      render: (val: SimulationShipmentRawResult) => {
        return <Text>{val.error}</Text>;
      },
    },
  ];

  const successColumns: ColumnsType<SimulationShipmentRawResult> = [
    {
      title: 'Selected Carrier Service Method',
      key: 'selectedCarrierServiceMethod',
      width: 280,
      align: 'left',
      render: (val: SimulationShipmentRawResult) => {
        return (
          carrierServiceMethodMap[val.carrierDecisionCarrierServiceMethodId]?.name ||
          val.carrierDecisionCarrierServiceMethodId
        );
      },
    },
    {
      title: 'Time in Transit Type',
      key: 'tnt',
      align: 'center',
      render: (val: SimulationShipmentRawResult) => {
        return (
          <>
            <Tag>{val.timeInTransitDataType}</Tag>
          </>
        );
      },
    },
    {
      title: 'Time in Transit Days',
      key: 'tnt',
      align: 'center',
      render: (val: SimulationShipmentRawResult) => {
        return val.timeInTransitDays;
      },
    },
  ];

  const commonColumns: ColumnsType<SimulationShipmentRawResult> = [
    {
      title: 'Origin',
      key: 'origin',
      align: 'left',
      render: (val: SimulationShipmentRawResult) => {
        return (
          <>
            {val.fromPostalCode}, {val.fromState} {val.fromCountryCode}
          </>
        );
      },
    },
    {
      title: 'Destination',
      key: 'destination',
      align: 'left',
      render: (val: SimulationShipmentRawResult) => {
        return (
          <>
            {val.destinationPostalCode}, {val.destinationState} {val.destinationCountryCode}
          </>
        );
      },
    },
    {
      title: 'Dimensions',
      key: 'dims',
      align: 'left',
      render: (val: SimulationShipmentRawResult) => {
        return (
          <>
            {val.packageLength}x{val.packageWidth}x{val.packageHeight}
            {val.linearUnit}
          </>
        );
      },
    },
    {
      title: 'Weight',
      key: 'weight',
      align: 'left',
      render: (val: SimulationShipmentRawResult) => {
        return (
          <>
            {val.totalWeight?.toFixed(2)}
            {val?.totalWeightUnit}
          </>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      align: 'left',
      fixed: 'right',
      render: (val: SimulationShipmentRawResult) => {
        return <ShipiumTagWithConfig configMap={shipiumTagMapStatus} tag={val.status} />;
      },
    },
    {
      title: 'View',
      key: 'view',
      align: 'left',
      responsive: ['xxl'],
      render: (val: SimulationShipmentRawResult) => {
        return (
          <Button type="link" onClick={() => selectShipment(val)}>
            <FileSearchOutlined style={{ fontSize: '16px' }} />
          </Button>
        );
      },
    },
  ];

  let columns: ColumnsType<SimulationShipmentRawResult> = [];

  switch (simulationRawResult?.options.status) {
    case 'success':
      columns = [...successColumns, ...commonColumns];
      break;
    case 'error':
      columns = [...errorColumns, ...commonColumns];
      break;
    default:
      columns = [...commonColumns];
      break;
  }

  const detailModal = () => {
    const title = selectedSimulatedShipment ? (
      <Space size={'large'}>
        <Text strong>
          Status: <ShipiumTagWithConfig configMap={shipiumTagMapStatus} tag={selectedSimulatedShipment.status} />
        </Text>
        <Text strong>
          Shipment Id: <Text code>{selectedSimulatedShipment.key}</Text>
        </Text>
        <Text strong>
          Reference Identifier: <Text code>{selectedSimulatedShipment.referenceIdentifier}</Text>
        </Text>
      </Space>
    ) : null;
    const error = selectedSimulatedShipment?.error ? selectedSimulatedShipment?.error || 'Unknown Error' : null;
    const response = selectedSimulatedShipment?.response
      ? JSON.stringify(selectedSimulatedShipment?.response)
      : error || 'N/A';
    const skipped = (
      <Alert
        message={'Shipment Skipped'}
        description="No matching mapping found for this shipment."
        type={'warning'}
        showIcon
      />
    );
    return (
      <Drawer
        width={'100%'}
        title={title}
        placement={'right'}
        closeIcon={null}
        open={!!selectedShipmentId}
        extra={
          <>
            <Button
              type={'primary'}
              danger
              style={{ backgroundColor: palette.Coral.dark, margin: 5 }}
              onClick={() => clearShipmentSelection()}
            >
              Close
            </Button>
          </>
        }
        headerStyle={{ padding: 0 }}
        onClose={() => clearShipmentSelection()}
      >
        {/* TODO It would be nice to show the mappings / why it was not mapped here */}
        {selectedSimulatedShipment?.status === 'skipped' && skipped}
        {selectedSimulatedShipment?.status === 'error' && (
          <>
            <Alert message={'Shipment Error'} description={error} type={'error'} showIcon />
            <StyledCodeMirror
              title={`Request`}
              initialValue={JSON.stringify(selectedSimulatedShipment?.request) || 'N/A'}
              readOnly={true}
              mode={'javascript'}
              full={true}
            />
          </>
        )}
        {selectedSimulatedShipment?.status === 'success' && (
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <StyledCodeMirror
                title={`Request`}
                initialValue={JSON.stringify(selectedSimulatedShipment?.request) || 'N/A'}
                readOnly={true}
                mode={'javascript'}
                full={true}
              />
            </Col>
            <Col span={12}>
              <StyledCodeMirror
                title={`Response`}
                initialValue={response}
                readOnly={true}
                mode={'javascript'}
                full={true}
              />
            </Col>
          </Row>
        )}
      </Drawer>
    );
  };

  return (
    <>
      <Table
        loading={loading}
        size={'small'}
        columns={columns}
        dataSource={simulationShipments}
        pagination={{
          pageSize: 50,
          total: simulationRawResult?.options.count || 0,
          position: ['topRight', 'bottomRight'],
          showSizeChanger: false,
          onChange(page, pageSize) {
            gotoPage(page);
          },
        }}
        bordered={false}
        className={classes.tableStyles}
      />
      {detailModal()}
    </>
  );
};
